/** Notification bell for the current user's background tasks.
 *
 *  Polls `/bgtasks/?mine=true` every 10 s while a console user is logged in
 *  (TanStack Query pauses the interval when the tab is unfocused — the default
 *  `refetchIntervalInBackground: false`). A badge counts everything that still
 *  wants attention: all active tasks, plus terminal tasks that are both unread
 *  (finished after the last "mark all read" sweep) and finished within the last
 *  12 h. The dropdown groups tasks by outcome and deep-links each into
 *  `/bgtasks/:id`; opening a task, or "View all", marks everything read.
 */
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as bgtasksApi from '@/api/bgtasks';
import { ACTIVE_STATES, TASK_STATE, type BackgroundTaskRecord } from '@/api/bgtasks';
import { useOptionalAuth } from '@/auth/AuthProvider';

import { useBgTaskReadState } from './useBgTaskReadState';
import { TaskStateChip, relativeTime } from './taskFormat';

/** Terminal task stays "fresh" (badge-worthy while unread) for this long. */
const FRESH_WINDOW_MS = 12 * 3600 * 1000;
const POLL_MS = 10_000;
const PAGE_SIZE = 20;

/** Epoch ms a task reached (or last touched) its current state. */
function finishedMs(t: BackgroundTaskRecord): number {
  return Date.parse(t.finished_ts || t.updated_ts);
}

const TERMINAL_STATES: number[] = [
  TASK_STATE.COMPLETED,
  TASK_STATE.FAILED,
  TASK_STATE.CANCELLED,
];

interface TaskGroup {
  key: string;
  label: string;
  tasks: BackgroundTaskRecord[];
}

export function BgTaskBell() {
  const auth = useOptionalAuth();
  const navigate = useNavigate();
  const { readSweepTs, markAllRead } = useBgTaskReadState();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const isAuthed = Boolean(auth?.user);

  const { data } = useQuery({
    queryKey: ['bgtasks', 'mine'],
    queryFn: () =>
      bgtasksApi.list({ mine: true, ordering: '-updated_ts', page_size: PAGE_SIZE }),
    refetchInterval: POLL_MS,
    enabled: isAuthed,
  });

  const tasks = useMemo(() => data?.results ?? [], [data]);

  // Badge: active tasks always count; terminal tasks count only while unread
  // AND finished within the fresh window. Recomputed on every render so the
  // 12 h window and the sweep are always evaluated against "now".
  const badgeCount = useMemo(() => {
    const now = Date.now();
    const active = tasks.filter((t) => ACTIVE_STATES.includes(t.state));
    const freshUnreadTerminal = tasks.filter(
      (t) =>
        TERMINAL_STATES.includes(t.state) &&
        finishedMs(t) > readSweepTs &&
        now - finishedMs(t) < FRESH_WINDOW_MS,
    );
    return active.length + freshUnreadTerminal.length;
  }, [tasks, readSweepTs]);

  // Dropdown groups (most-recent-first is already guaranteed by the ordering).
  const groups = useMemo<TaskGroup[]>(() => {
    const running = tasks.filter((t) => ACTIVE_STATES.includes(t.state));
    const failed = tasks.filter(
      (t) => t.state === TASK_STATE.FAILED || t.state === TASK_STATE.CANCELLED,
    );
    const succeeded = tasks.filter((t) => t.state === TASK_STATE.COMPLETED);
    return [
      { key: 'running', label: 'Running', tasks: running },
      { key: 'failed', label: 'Failed', tasks: failed },
      { key: 'succeeded', label: 'Succeeded', tasks: succeeded },
    ].filter((g) => g.tasks.length > 0);
  }, [tasks]);

  if (!isAuthed) return null;

  const closeMenu = () => setAnchor(null);

  const openTask = (id: string) => {
    markAllRead();
    closeMenu();
    navigate(`/bgtasks/${id}`);
  };

  const viewAll = () => {
    markAllRead();
    closeMenu();
    navigate('/bgtasks');
  };

  return (
    <>
      <Tooltip title="Background tasks">
        <IconButton
          color="inherit"
          aria-label={
            badgeCount > 0
              ? `Background tasks (${badgeCount} need attention)`
              : 'Background tasks'
          }
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          <Badge badgeContent={badgeCount} color="error" invisible={badgeCount === 0}>
            {badgeCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: '90vw' } } }}
      >
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            Background tasks
          </Typography>
          {badgeCount > 0 && (
            <Typography
              component="button"
              variant="caption"
              onClick={markAllRead}
              sx={{
                border: 0,
                background: 'none',
                cursor: 'pointer',
                color: 'primary.main',
                p: 0,
                font: 'inherit',
                fontSize: 12,
              }}
            >
              Mark all read
            </Typography>
          )}
        </Box>
        <Divider />

        {groups.length === 0 ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No recent tasks.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
            {groups.map((group) => (
              <List
                key={group.key}
                dense
                disablePadding
                subheader={
                  <ListSubheader disableSticky sx={{ lineHeight: '32px', bgcolor: 'transparent' }}>
                    {group.label}
                  </ListSubheader>
                }
              >
                {group.tasks.map((task) => (
                  <ListItemButton key={task.id} onClick={() => openTask(task.id)}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                            {task.task_label}
                          </Typography>
                          <TaskStateChip task={task} />
                        </Box>
                      }
                      secondary={relativeTime(task.finished_ts || task.updated_ts)}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                ))}
              </List>
            ))}
          </Box>
        )}

        <Divider />
        <MenuItem onClick={viewAll}>
          <ListItemText
            primary="View all background tasks"
            primaryTypographyProps={{ variant: 'body2', color: 'primary', align: 'center' }}
          />
        </MenuItem>
      </Menu>
    </>
  );
}
